<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { TemplateEventVault } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, ref } from "vue"
import { applyEventTemplate, clearEventTemplate, getCustomTemplate, useListEventsForTemplate } from "@/api"
import { Button, ButtonLink, NoData, Table, Toast } from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, d } = useTranslation()

const eventTemplateId = useConvertedRouteParam("id", "/template/[id]/assign", value => Number(value))

const queryClient = useQueryClient()
const errorToast = ref<ToastHtmlElement | null>(null)
const successToast = ref<ToastHtmlElement | null>(null)
const urlParam = useUrlSearchParams<{ hostAccountId?: number }>()

const { data: customTemplate, isLoading: isLoadingTemplate } = useQuery({
  queryKey: ["getCustomTemplate", eventTemplateId.value],
  queryFn: () => getCustomTemplate(eventTemplateId.value),
  enabled: !!eventTemplateId.value,
})

const {
  data: eventVaults,
  isLoading: isLoadingEventVaults,
} = useListEventsForTemplate(eventTemplateId.value, urlParam.hostAccountId)

const headers: ITableHeader<TemplateEventVault & { actions?: unknown }>[] = [
  { label: t("choose_event_for_template_page.header.title"), value: "title" },
  { label: t("choose_event_for_template_page.header.date"), value: "eventDate" },
  { label: t("choose_event_for_template_page.header.action"), value: "actions" },
]

const { mutate: applyTemplate, isPending: isAplyingTemplate } = useMutation({
  mutationFn: (eventVaultId?: number) => applyEventTemplate(customTemplate.value?.id, eventVaultId),
  onSuccess: async () => {
    invalidateEventList()
    showSuccesToast(t("choose_event_for_template_page.success_apply", { theme: customTemplate.value?.name ?? "-" }))
  },
  onError: () => showErrorToast(),
})

const { mutate: removeTemplate, isPending: isRemovingTemplate } = useMutation({
  mutationFn: (eventVaultId?: number) => clearEventTemplate(customTemplate.value?.id, eventVaultId),
  onSuccess: async () => {
    invalidateEventList()
    showSuccesToast(t("choose_event_for_template_page.success_clear", { theme: customTemplate.value?.name ?? "-" }))
  },
  onError: () => showErrorToast(),
})

const isApplyingChanges = computed(() => isAplyingTemplate.value || isRemovingTemplate.value)

async function invalidateEventList() {
  await queryClient.invalidateQueries({ queryKey: ["listEventsForTemplate", eventTemplateId.value] })
}

function showErrorToast() {
  return errorToast.value?.showToast()
}
function showSuccesToast(message: string) {
  return successToast.value?.showToast({ title: t("success"), content: message, icon: "CircleCheck" })
}
</script>

<template>
  <SidebarLayout>
    <template #header>
      <div class="bg-white rounded-xl flex justify-between items-end gap-4">
        <div class="grow">
          <h3 class="text-[#757575] text-sm">
            {{ t('choose_event_for_template_page.your_template_label') }}
          </h3>
          <div v-if="isLoadingTemplate" class="gpx-skeleton h-10" />
          <h1 v-else class="font-comfortaa text-xl font-semibold" data-test="template-name">
            {{ customTemplate?.name ?? "-" }}
          </h1>
        </div>
        <ButtonLink v-if="customTemplate" :to="`/template/${eventTemplateId}/edit`" data-test="template-edit-link" prefix-icon="Edit">
          <span class="hidden md:block">{{ t('choose_event_for_template_page.edit_button') }}</span>
        </ButtonLink>
      </div>
    </template>
    <template #content>
      <div class="container mt-6">
        <div class="flex flex-col gap-2 mb-10">
          <h1 class="text-2xl font-comfortaa font-bold">
            {{ t('choose_event_for_template_page.title') }}
          </h1>
          <div class="text-label">
            {{ t('choose_event_for_template_page.message') }}
          </div>
        </div>

        <Table :headers="headers" :data="eventVaults as Array<TemplateEventVault & { actions?: unknown }>" :is-loading="isLoadingEventVaults" compact>
          <template #empty>
            <tbody>
              <tr>
                <td :colspan="headers.length + 1">
                  <NoData
                    :headline="t('choose_event_for_template_page.no_events_headline')"
                    :content="t('choose_event_for_template_page.no_events_content')" hide-button
                  />
                </td>
              </tr>
            </tbody>
          </template>
          <template #data-title="{ data }">
            <div class="font-semibold font-montserrat" data-test="event-title">
              {{ data.title }}
            </div>
          </template>
          <template #data-eventDate="{ data }">
            <span v-if="data.eventDate">{{ d(data.eventDate, "short") }}</span>
          </template>
          <template #data-actions="{ data }">
            <Button
              :disabled="isApplyingChanges" :variant="data.isApplied ? 'danger' : 'tertiary'"
              :data-test="data.isApplied ? 'remove-template-btn' : 'apply-template-btn'" @click="() => data.isApplied
                ? removeTemplate(data.id)
                : applyTemplate(data.id)"
            >
              {{ data.isApplied
                ? t('choose_event_for_template_page.cancel_btn')
                : t('choose_event_for_template_page.apply_btn') }}
            </Button>
          </template>
        </Table>
      </div>
    </template>
  </SidebarLayout>
  <Toast ref="successToast" variant="success" />
  <Toast ref="errorToast" variant="danger" />
</template>
